// src/pages/notifications/ChannelListPage.tsx
// Notification channels list with type/scope filters.

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsChannel, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardContent, Badge, ErrorAlert,
} from '@react/components';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { formatChannelType, formatChannelScope } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import { NotificationChannelTypeValues } from '@core/types/notifications/NotificationChannelType';
import { ChannelScopeValues } from '@core/types/notifications/ChannelScope';
import type { NotificationChannel } from '@core/types/notifications/NotificationChannel';
import { Select } from '@/components/Select';

export const ChannelListPage = observer(() => {
  const store = useNotificationsChannel();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') ?? '');
  const [scopeFilter, setScopeFilter] = useState(searchParams.get('scope') ?? '');

  useEffect(() => {
    store.ListChannels({ limit: 20 } as never);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (search) params.q = search;
    if (typeFilter) params.type = typeFilter;
    if (scopeFilter) params.scope = scopeFilter;
    setSearchParams(params);
    store.ListChannels({ limit: 20, search, type: typeFilter, scope: scopeFilter } as never);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('notifications.deleteChannelConfirm'))) return;
    try {
      await store.DeleteChannel(id);
    } catch {
      // handled by store
    }
  };

  const items = store.items as NotificationChannel[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('notifications.channels')}</h2>
        <Link to="/notifications/channels/new"><Button>{t('notifications.createChannel')}</Button></Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardHeader>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} fullWidth={false}>
              <option value="">{t('common.all')} {t('notifications.channelType')}</option>
              {NotificationChannelTypeValues.filter(v => v !== 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED').map((s) => {
                const ct = formatChannelType(s);
                return <option key={s} value={s}>{t(ct.labelKey)}</option>;
              })}
            </Select>
            <Select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} fullWidth={false}>
              <option value="">{t('common.all')} {t('notifications.scope')}</option>
              {ChannelScopeValues.filter(v => v !== 'CHANNEL_SCOPE_UNSPECIFIED').map((s) => {
                const sc = formatChannelScope(s);
                return <option key={s} value={s}>{t(sc.labelKey)}</option>;
              })}
            </Select>
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardHeader>
        <CardContent>
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.name')}</TableHead>
                <TableHead>{t('notifications.channelType')}</TableHead>
                <TableHead>{t('notifications.scope')}</TableHead>
                <TableHead>{t('notifications.enabled')}</TableHead>
                <TableHead>{t('notifications.default')}</TableHead>
                <TableHead>{t('notifications.healthStatus')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && !store.isLoading && <TableEmpty colSpan={7} message={t('notifications.noChannels')} />}
              {items.map((ch) => {
                const chType = formatChannelType(ch.type ?? '');
                const scope = formatChannelScope(ch.scope ?? '');
                return (
                  <TableRow key={ch.entity?.id}>
                    <TableCell>
                      <Link to={`/notifications/channels/${ch.entity?.id}`} className="text-sm font-medium text-primary hover:text-primary/80">{ch.name}</Link>
                    </TableCell>
                    <TableCell className="text-sm"><span className="inline-flex items-center gap-1.5"><NotificationIcon name={chType.icon} className="h-4 w-4" />{t(chType.labelKey)}</span></TableCell>
                    <TableCell className="text-sm">{t(scope.labelKey)}</TableCell>
                    <TableCell><Badge variant={ch.is_enabled ? 'success' : 'secondary'}>{ch.is_enabled ? t('common.yes') : t('common.no')}</Badge></TableCell>
                    <TableCell><Badge variant={ch.is_default ? 'primary' : 'secondary'}>{ch.is_default ? t('common.yes') : t('common.no')}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">-</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Link to={`/notifications/channels/${ch.entity?.id}`}><Button variant="ghost" size="sm">{t('common.open')}</Button></Link>
                        <Link to={`/notifications/channels/${ch.entity?.id}/edit`}><Button variant="ghost" size="sm">{t('common.edit')}</Button></Link>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(ch.entity?.id ?? '')}>{t('common.delete')}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {store.hasMore && (
            <div className="mt-4 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('common.showingItems', { count: items.length })}</span>
              <Button variant="secondary" size="sm" onClick={() => store.ListChannels({ limit: 20, cursor: store.pageInfo?.next_cursor } as never)} isLoading={store.isLoading}>{t('common.next')}</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default ChannelListPage;
